import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import PublicLayout from "../components/PublicLayout";
import { ProgressBar } from "../components/ProgressBar";
import { api } from "../lib/api";
import { Search, MapPin, FileCheck } from "lucide-react";

export default function Schools() {
  const [schools, setSchools] = useState([]);
  const [q, setQ] = useState("");

  useEffect(() => { api.get("/public/schools").then(r => setSchools(r.data)).catch(()=>{}); }, []);

  const filtered = schools.filter(s =>
    !q || s.name?.toLowerCase().includes(q.toLowerCase()) || s.address?.toLowerCase().includes(q.toLowerCase())
  );

  return (
    <PublicLayout>
      <section className="max-w-7xl mx-auto px-6 md:px-10 pt-16 pb-8">
        <div className="grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <div className="bb-overline text-[var(--bb-terracotta)] mb-4">ALL SCHOOLS</div>
            <h1 className="font-serif text-5xl md:text-6xl tracking-tight text-[var(--bb-forest)] max-w-3xl">Pick a classroom to light up.</h1>
            <p className="mt-5 text-[var(--bb-ink)]/75 max-w-2xl">Each card is a real school with real numbers. Progress bars update as sponsors purchase book sets. Once a school is fully sponsored, we print immediately.</p>

            <div className="mt-8 flex items-center gap-3 max-w-md bb-card px-4 py-2">
              <Search size={16} className="text-[var(--bb-muted)]"/>
              <input
                value={q}
                onChange={(e)=>setQ(e.target.value)}
                placeholder="Search by school or district"
                className="flex-1 bg-transparent outline-none text-sm py-2"
                data-testid="schools-search"
              />
            </div>
          </div>

          <div className="lg:col-span-5" data-testid="schools-hero-image-wrap">
            <figure className="relative">
              <div className="rounded-2xl overflow-hidden shadow-[0_24px_48px_-18px_rgba(28,63,52,0.45)] ring-1 ring-[var(--bb-border)] bg-[var(--bb-sand)]">
                <img
                  src="https://customer-assets.emergentagent.com/job_bala-donor-hub/artifacts/miek1uj4_School_Desk.png"
                  alt="A Karnataka classroom with desks awaiting Bala Basava comics — some delivered, others marked Waiting / ಕಾದಿದೆ."
                  loading="lazy"
                  className="w-full h-full object-cover"
                  data-testid="schools-hero-image"
                />
              </div>
              <figcaption className="mt-3 text-xs text-[var(--bb-muted)] italic leading-relaxed">
                Each desk marked <span className="text-[var(--bb-terracotta)] font-medium">"Waiting"</span> is a child still hoping. Every sponsorship turns one tag into a book.
              </figcaption>
            </figure>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-10 pb-24">
        {filtered.length === 0 ? (
          <div className="bb-card p-10 text-center text-[var(--bb-muted)]">No schools match. Try another search.</div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map((s, i) => (
              <Link to={`/schools/${s.school_id}`} key={s.school_id} className="bb-card p-6 block bb-fade-up" style={{ animationDelay: `${0.05*i}s` }} data-testid={`schools-card-${i}`}>
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <div className="bb-tag bb-tag-green">{s.status}</div>
                    {s.mou_path && <div className="bb-tag" data-testid={`schools-mou-badge-${i}`}><FileCheck size={10}/> MoU</div>}
                  </div>
                  <div className="text-xs text-[var(--bb-muted)] flex items-center gap-1"><MapPin size={12}/>{(s.address||"").split(",")[0]}</div>
                </div>
                {s.logo_path && (
                  <img src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${s.logo_path}`} alt="" className="w-14 h-14 rounded-full object-cover border border-[var(--bb-border)] mb-3"/>
                )}
                <h3 className="font-serif text-2xl text-[var(--bb-forest)] leading-tight mb-2 line-clamp-2">{s.name}</h3>
                <div className="grid grid-cols-2 gap-3 my-5">
                  <MiniStat label="Students" value={s.total_students} />
                  <MiniStat label="Books needed" value={s.total_books_required} />
                </div>
                <ProgressBar value={s.total_books_funded || 0} total={s.total_books_required || 0} />
                <div className="mt-5 text-sm font-medium text-[var(--bb-terracotta)]">Sponsor Books →</div>
              </Link>
            ))}
          </div>
        )}
      </section>
    </PublicLayout>
  );
}

function MiniStat({ label, value }) {
  return (
    <div>
      <div className="bb-overline text-[var(--bb-muted)]">{label}</div>
      <div className="font-serif text-2xl text-[var(--bb-forest)]">{value}</div>
    </div>
  );
}
